import { useCallback, useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

export const SRP_STAGE_OPTIONS = [1, 2, 3] as const;
export const ALIGNER_SEVERITY_OPTIONS = ['MILD', 'MODERATE'] as const;

export function useAlignerSeverityLabels(): Record<
  (typeof ALIGNER_SEVERITY_OPTIONS)[number],
  string
> {
  const { t } = useLingui();
  return useMemo(
    () => ({
      MILD: t`mild`,
      MODERATE: t`mild and moderate`,
    }),
    [t],
  );
}

export interface TppProviderSettings {
  crownDmfThreshold: number;
  srpPerioStageThreshold: (typeof SRP_STAGE_OPTIONS)[number];
  incipientEnabled: boolean;
  alignersEnabled: boolean;
  alignersSeverityThreshold?: (typeof ALIGNER_SEVERITY_OPTIONS)[number];
}

export interface TppProviderSettingsResponse extends TppProviderSettings {
  updatedAt?: string;
  updatedByName?: string;
  updatedByEmail?: string;
}

/** Defensive fallback only — the backend always returns providerSettings via its
 *  own 3-tier default chain (provider → practice → hardcoded). These values align
 *  with the backend's hardcoded defaults so they match if this ever runs. */
export const DEFAULT_PROVIDER_SETTINGS: TppProviderSettings = {
  crownDmfThreshold: 50,
  srpPerioStageThreshold: 2,
  incipientEnabled: false,
  alignersEnabled: false,
} as const;

/** Metric values for one Core Four category (percent and planned X of Y). */
export interface CoreFourMetricItem {
  percent: number;
  planned: number;
  opportunities: number;
}

export interface CoreFourMetrics {
  crown: CoreFourMetricItem;
  srp: CoreFourMetricItem;
  incipient: CoreFourMetricItem;
  aligners: CoreFourMetricItem;
}

const DEFAULT_METRICS: CoreFourMetrics = {
  crown: { percent: 0, planned: 0, opportunities: 0 },
  srp: { percent: 0, planned: 0, opportunities: 0 },
  incipient: { percent: 0, planned: 0, opportunities: 0 },
  aligners: { percent: 0, planned: 0, opportunities: 0 },
};

interface TppMetricCategory {
  planned: number;
  scheduled: number;
  completed: number;
  unplanned: number;
}

type TppMetricCategoryOptional = Partial<TppMetricCategory> | undefined;

interface GetTppResponse {
  providerSettings: TppProviderSettingsResponse;
  tppData: {
    crown: TppMetricCategory;
    srp: TppMetricCategory;
    incipient?: TppMetricCategoryOptional;
    aligners?: TppMetricCategoryOptional;
  };
}

function metricCategoryToItem(
  category: TppMetricCategoryOptional,
): CoreFourMetricItem {
  if (!category) {
    return { percent: 0, planned: 0, opportunities: 0 };
  }
  const planned = category.planned ?? 0;
  const scheduled = category.scheduled ?? 0;
  const completed = category.completed ?? 0;
  const unplanned = category.unplanned ?? 0;
  const opportunities = planned + scheduled + completed + unplanned;
  const numerator = planned + scheduled + completed;
  const percent =
    opportunities > 0 ? Math.round((numerator / opportunities) * 100) : 0;
  return { percent, planned: numerator, opportunities };
}

const METRIC_KEYS: (keyof CoreFourMetrics)[] = ['crown', 'srp', 'incipient', 'aligners'];

interface GetCoreFourTPPVariables {
  providerId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  enabled?: boolean;
}

export function useGetCoreFourTPP({
  providerId,
  startDate,
  endDate,
  enabled = true,
}: GetCoreFourTPPVariables = {}) {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  const queryFn = useCallback(async (): Promise<GetTppResponse> => {
    return makeAPIRequestReactQuery<GetTppResponse>(
      endpoints.tpp,
      { providerVideaId: providerId, start: startDate, end: endDate },
      'GET',
      {
        headers: {
          org: currentOrganizationId,
          practice: currentPracticeId,
        },
      },
    );
  }, [providerId, startDate, endDate, currentOrganizationId, currentPracticeId]);

  const result = useQuery<GetTppResponse>({
    queryKey: [endpoints.tpp, providerId ?? 'all', startDate, endDate],
    queryFn,
    enabled: enabled && !!currentPracticeId && !!currentOrganizationId,
  });

  const providerSettings =
    result.data?.providerSettings ?? DEFAULT_PROVIDER_SETTINGS;
  const metrics = result.data
    ? METRIC_KEYS.reduce((acc, key) => {
        acc[key] = metricCategoryToItem(result.data!.tppData[key]);
        return acc;
      }, {} as CoreFourMetrics)
    : DEFAULT_METRICS;
  const lastUpdated = result.data?.providerSettings?.updatedAt
    ? {
        date: result.data.providerSettings.updatedAt,
        by: result.data.providerSettings.updatedByName ?? '',
      }
    : null;

  return { providerSettings, metrics, lastUpdated, result };
}

export interface UpdateCoreFourTPPVariables {
  providerSettings: TppProviderSettings;
  providerId?: string | null;
}

export function useUpdateCoreFourTPP() {
  const queryClient = useQueryClient();
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useMutation<TppProviderSettingsResponse, Error, UpdateCoreFourTPPVariables>({
    mutationFn: async ({ providerSettings, providerId }) => {
      return makeAPIRequestReactQuery<TppProviderSettingsResponse>(
        `${endpoints.tppProviderSettings}/${providerId}`,
        {},
        'PUT',
        {
          data: providerSettings,
          headers: {
            org: currentOrganizationId,
            practice: currentPracticeId,
          },
        },
      );
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<GetTppResponse>(
        { queryKey: [endpoints.tpp, variables.providerId ?? 'all'] },
        (old) => old ? { ...old, providerSettings: data } : old,
      );
      queryClient.invalidateQueries({
        queryKey: [endpoints.tpp, variables.providerId ?? 'all'],
      });
    },
  });
}
